import React, { useMemo, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";

import { T } from "../../constants";
import { useUi, cx } from "../../ui";
import Overlay from "../../components/Overlay";
import api from "../../../../lib/api";

/* Sits above the walk-up modal's own Overlay (z 1300). */
const CARD_OVERLAY_Z = 1400;

const useStyles = makeStyles({
  row: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    gap: 12,
    fontSize: 12,
  },
  rowLabel: { color: T.mutedFg },
  rowValue: { color: T.fg, fontWeight: 600 },
  rows: { display: "flex", flexDirection: "column", gap: 6 },
  captured: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
  },
  onFile: { fontSize: 11, color: T.successText, marginTop: 4 },
  prompt: { margin: "0 0 8px", fontSize: 11, color: T.mutedFg },
  promptError: { color: T.danger },
  linkBtn: {
    border: "none",
    background: "transparent",
    padding: 0,
    fontFamily: "inherit",
    fontSize: 11,
    fontWeight: 600,
    color: T.primary,
    cursor: "pointer",
  },

  dialog: {
    width: "100%",
    maxWidth: 440,
    borderRadius: 12,
    border: `1px solid ${T.border}`,
    background: T.elevated,
    padding: 24,
    boxShadow: "0 20px 50px rgba(0,0,0,0.25)",
  },
  heading: { margin: "2px 0 16px", fontSize: 20, fontWeight: 700, color: T.fg },
  error: {
    margin: "0 0 12px",
    borderRadius: 6,
    border: `1px solid ${T.danger}`,
    background: T.dangerTint,
    padding: "8px 10px",
    fontSize: 12,
    color: T.fg,
  },
  footer: { marginTop: 20, display: "flex", justifyContent: "flex-end", gap: 8 },
  cancelBtn: { border: "none", background: "transparent" },
});

function cardOf(method) {
  try {
    return typeof method === "string" ? JSON.parse(method)?.card : method?.card;
  } catch (err) {
    return null;
  }
}

/* Methods saved via Stripe Link carry no card number, so key off last4 rather
 * than brand to avoid rendering "•••• undefined". */
function formatNumber(method) {
  const card = cardOf(method);
  if (!card?.last4) return "Link account";
  return `•••• ${card.last4}`;
}

function formatExpiry(method) {
  const card = cardOf(method);
  if (!card?.last4) return "—";
  return `${String(card.exp_month).padStart(2, "0")} / ${String(card.exp_year).slice(-2)}`;
}

/**
 * Card capture for the walk-up modal.
 */
export default function CardInput({
  appConfig,
  currency = "AUD",
  country,
  installationId,
  guestEmail,
  paymentMethod,
  confirmed,
  onConfirmed,
  readOnly = false,
}) {
  const s = useStyles();
  const ui = useUi();
  const [modalOpen, setModalOpen] = useState(false);
  const [error, setError] = useState("");

  /* Per-country publishable key, falling back to AU. */
  const countryUpper = country ? country.toUpperCase() : null;
  const publishableKey =
    (countryUpper === "US" && appConfig?.STRIPE_PUBLISHABLE_KEY_US) ||
    (countryUpper === "NZ" && appConfig?.STRIPE_PUBLISHABLE_KEY_NZ) ||
    appConfig?.STRIPE_PUBLISHABLE_KEY;

  /* Memoized so <Elements> doesn't remount and wipe in-progress card entry
   * every time modalOpen or error changes. */
  const stripeInstance = useMemo(() => loadStripe(publishableKey), [publishableKey]);
  const options = useMemo(
    () => ({ mode: "setup", currency: currency.toLowerCase() }),
    [currency],
  );

  return (
    <div>
      {paymentMethod ? (
        <div className={s.captured}>
          <div className={s.rows}>
            <div className={s.row}>
              <span className={s.rowLabel}>Card</span>
              <span className={s.rowValue}>{formatNumber(paymentMethod)}</span>
            </div>
            <div className={s.row}>
              <span className={s.rowLabel}>Expiry</span>
              <span className={s.rowValue}>{formatExpiry(paymentMethod)}</span>
            </div>
          </div>
          <button onClick={() => setModalOpen(true)} className={s.linkBtn}>
            Update card
          </button>
        </div>
      ) : confirmed ? (
        <div className={s.captured}>
          <div className={s.onFile}>✓ Card authorised</div>
          <button onClick={() => setModalOpen(true)} className={s.linkBtn}>
            Update card
          </button>
        </div>
      ) : readOnly ? (
        <p className={s.prompt}>No card on file.</p>
      ) : (
        <div>
          <p className={cx(s.prompt, error && s.promptError)}>
            {error || "Add the guest's card to authorise the parking fee."}
          </p>
          <button onClick={() => setModalOpen(true)} className={ui.ghostBtn}>
            + Add card
          </button>
        </div>
      )}

      {modalOpen && (
        <Elements stripe={stripeInstance} options={options}>
          <CardModal
            onClose={() => setModalOpen(false)}
            onConfirmed={onConfirmed}
            installationId={installationId}
            guestEmail={guestEmail}
            setOuterError={setError}
          />
        </Elements>
      )}
    </div>
  );
}

function CardModal({
  onClose,
  onConfirmed,
  installationId,
  guestEmail,
  setOuterError,
}) {
  const s = useStyles();
  const ui = useUi();
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function submit() {
    if (!stripe || !elements || loading) return;

    setLoading(true);

    const { error: submitError } = await elements.submit();
    if (submitError) {
      setError("Please enter valid card details.");
      setLoading(false);
      return;
    }

    let data;
    try {
      ({ data } = await api.createIntent(installationId, guestEmail));
    } catch (err) {
      /* Without this catch the modal stays stuck on the spinner and the
       * rejection surfaces as an unhandled promise error. */
      setLoading(false);
      setError("Unable to initialise payment setup. Please try again.");
      return;
    }

    const { client_secret: clientSecret, customer_id: stripeCustomerId } = data;

    const { error: confirmError } = await stripe.confirmSetup({
      elements,
      clientSecret,
      redirect: "if_required",
      confirmParams: { return_url: window.location.origin },
    });

    if (confirmError) {
      setLoading(false);
      setError("Invalid payment method. Please enter valid card details.");
      return;
    }

    setLoading(false);
    setOuterError("");
    onConfirmed(stripeCustomerId);
    onClose();
  }

  return (
    <Overlay onClose={onClose} z={CARD_OVERLAY_Z}>
      <div className={s.dialog}>
        <div className={ui.eyebrow}>Payment</div>
        <h3 className={s.heading}>Add card details</h3>

        {error && <div className={s.error}>{error}</div>}

        <PaymentElement />

        <div className={s.footer}>
          <button onClick={onClose} className={cx(ui.mutedBtn, s.cancelBtn)}>
            Cancel
          </button>
          <button onClick={submit} disabled={loading} className={ui.primaryBtn}>
            {loading ? "Authorising…" : "Add card"}
          </button>
        </div>
      </div>
    </Overlay>
  );
}
